import type {
    ServiceDiagnosticsBundle,
    ServiceDiagnosticsResponse,
    ServiceDiagnosticsSection,
} from '@web/vin-sessions/types';

import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { Button, Icon, TextInputHelper, Typography } from '@packages/ui/shared';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { docsService, vinSessionService } from '@web/services/singletons';
import { Eye, Favourite, FavouriteOpen, Share } from '@web/vin-sessions/assets/Icons';
import { PublicationType } from '@web/vin-sessions/components/stis-publications/publication-type-icons';
import { ToastSuccessMessage } from '@web/vin-sessions/components/toast-success';
import {
    fetchCdnDocumentUrlByDocId,
    openDocumentTab,
    resolveDocumentUrl,
} from '@web/vin-sessions/utils/open-document-tab';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ActionIconButton } from './action-icon-button';
import { useSdBundleSearch } from './hooks/use-sd-bundle-search';
import { SdCriteriaPanel } from './sd-criteria-panel';
import { SdPreviewModal } from './sd-preview-modal';
import {
    DEFAULT_SD_SORT,
    nextSort,
    pruneCarline,
    type SdFilters,
    type SdSearchType,
    type SdSortColumn,
    shouldResetQuery,
    toCarlineOptions,
    toModelYearOptions,
} from './sd-search.utils';

const logger = getLogger('service-diagnostics');

/** The same three sizes the publications list offers. */
const PAGE_SIZES = [10, 25, 50];

/** Pages listed in the keyword preview before the manual itself is opened. */
const PREVIEW_SIZE = 10;

const TOAST_MS = 3000;

/** The underline the publications tab bar puts under the selected tab. */
const SELECTED_TAB_STYLE = {
    borderBottomColor: colors.blueOne,
    borderBottomWidth: 2,
    color: colors.blueOne,
    paddingBottom: 5,
} as const;

const SKELETON_ROW_KEYS = Array.from({ length: 6 }, (_, index) => `sd-search-skeleton-${index}`);

type Props = {
    /** The browse-grid response, which also supplies the filter options. */
    diagnostics: ServiceDiagnosticsResponse;
    /** The pop-up guidance banner, rendered under the tabs rather than above them. */
    guidanceBanner?: ReactNode;
    /** The pop-up settings pill, rendered at the end of the tab row as Publications does. */
    helpPill?: ReactNode;
    onPopupBlocked: () => void;
};

const formatDate = (isoDate?: null | string) =>
    isoDate
        ? new Date(isoDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
          })
        : '-';

const useStyles = createUseStyles({
    screen: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    tabs: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
    },
    tabsEnd: {
        alignItems: 'center',
        display: 'flex',
        marginLeft: 'auto',
    },
    tab: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        padding: 0,
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    /** The publications search row: the box takes what is left of a fixed actions column. */
    searchRow: {
        alignItems: 'center',
        display: 'grid',
        gap: 34,
        gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 440px)',
        paddingRight: 21,
        '@media (max-width: 700px)': {
            gap: 12,
            gridTemplateColumns: 'minmax(0, 1fr)',
            paddingRight: 0,
        },
    },
    searchInput: {
        minWidth: 0,
        position: 'relative',
    },
    searchActions: {
        display: 'grid',
        gap: 12,
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    tableWrap: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        overflowX: 'auto',
    },
    table: {
        borderCollapse: 'collapse',
        minWidth: 900,
        width: '100%',
    },
    th: {
        backgroundColor: colors.grayOne,
        borderBottom: `1px solid ${colors.grayThree}`,
        borderRight: `1px solid ${colors.grayThree}`,
        '&:last-child': {
            borderRight: 'none',
        },
        color: colors.textDarkPrimary,
        fontSize: 14,
        fontWeight: 500,
        padding: '14px 16px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    td: {
        borderBottom: `1px solid ${colors.grayThree}`,
        color: colors.textDarkPrimary,
        fontSize: 14,
        padding: '14px 16px',
        verticalAlign: 'middle',
    },
    sortButton: {
        alignItems: 'center',
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        gap: 6,
        padding: 0,
        '&:disabled': {
            cursor: 'default',
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    typeCell: {
        alignItems: 'center',
        display: 'flex',
        gap: 10,
        whiteSpace: 'nowrap',
    },
    titleButton: {
        background: 'none',
        border: 'none',
        color: colors.blueLink,
        cursor: 'pointer',
        fontSize: 14,
        padding: 0,
        textAlign: 'left',
        '&:hover': {
            color: colors.blueLinkHover,
            textDecoration: 'underline',
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    actions: {
        alignItems: 'center',
        display: 'flex',
        gap: 5,
    },
    empty: {
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '48px 24px',
        textAlign: 'center',
    },
    emptyTitle: {
        color: colors.textDarkPrimary,
        fontSize: 17,
        fontWeight: 600,
    },
    emptyBody: {
        color: colors.textDarkSecondary,
        fontSize: 14,
        maxWidth: 460,
    },
    emptyAction: {
        background: 'none',
        border: 'none',
        color: colors.blueOne,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        marginTop: 4,
        padding: 0,
    },
    footer: {
        alignItems: 'center',
        color: colors.textDarkSecondary,
        display: 'flex',
        flexWrap: 'wrap',
        fontSize: 14,
        gap: 16,
        justifyContent: 'space-between',
    },
    pageButtons: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
    },
    pageButton: {
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 2,
        color: colors.blueOne,
        cursor: 'pointer',
        display: 'inline-flex',
        fontSize: 14,
        gap: 4,
        justifyContent: 'center',
        minWidth: 36,
        padding: '6px 10px',
        '&:disabled': {
            color: colors.grayFive,
            cursor: 'not-allowed',
        },
    },
    pageButtonActive: {
        backgroundColor: colors.blueOne,
        borderColor: colors.blueOne,
        color: colors.white,
        fontWeight: 700,
    },
    pageSize: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 2,
        color: colors.textDarkPrimary,
        fontSize: 14,
        height: 36,
        padding: '0 8px',
    },
    toastLayer: {
        bottom: 0,
        left: 0,
        pointerEvents: 'none',
        position: 'fixed',
        right: 0,
        top: 0,
        zIndex: 10001,
    },
});

/**
 * Search Manuals: the Service Diagnostics half of STIS Publications, laid out the same way. The
 * tabs come first, then the criteria panel, then the keyword box with Search and Clear all, then
 * the results table and its pager.
 *
 * The keyword is searched when Search is pressed rather than while typing, so a corpus-wide scan
 * runs once per intention instead of once per keystroke.
 *
 * @param props the browse-grid response and the pop-up-blocked callback
 * @returns the screen
 */
export const SearchManuals: FC<Props> = ({ diagnostics, guidanceBanner, helpPill, onPopupBlocked }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { account, token } = useAuthState();

    // What the box and the panel hold, before Search is pressed
    const [draftQuery, setDraftQuery] = useState('');
    const [draftFilters, setDraftFilters] = useState<SdFilters>({});
    // What the list is showing. Leaving the page and coming back starts a fresh search, the way
    // STIS Publications does, rather than reopening on someone's half-finished keyword.
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState<SdFilters>({});
    const [searchType, setSearchType] = useState<SdSearchType>('ALL');
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [sort, setSort] = useState(DEFAULT_SD_SORT);
    const [previewBundle, setPreviewBundle] = useState<ServiceDiagnosticsBundle>();
    const [previewSections, setPreviewSections] = useState<ServiceDiagnosticsSection[]>([]);
    const [previewTotal, setPreviewTotal] = useState(0);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewPage, setPreviewPage] = useState(0);
    const [toast, setToast] = useState<{ iconName: string; message: string }>();
    const toastTimer = useRef<ReturnType<typeof setTimeout>>();
    /** Only the newest preview request may write to the modal. */
    const previewRequest = useRef(0);

    const modelYearOptions = useMemo(() => toModelYearOptions(diagnostics), [diagnostics]);
    const carlineOptions = useMemo(
        () => toCarlineOptions(diagnostics, draftFilters.modelYear),
        [diagnostics, draftFilters.modelYear]
    );

    const { bundles, hasFailed, isKeywordSearch, isLoading, setFavorite, totalElements } = useSdBundleSearch({
        enabled: Boolean(account),
        filters,
        page,
        pageSize,
        query,
        searchType,
        sort,
    });

    useEffect(() => {
        setPage(0);
    }, [query, searchType, filters.modelYear, filters.carlineId, pageSize, sort]);

    // A stored carline can belong to a year the corpus no longer has
    useEffect(() => {
        setDraftFilters((previous) => pruneCarline(diagnostics, previous));
        setFilters((previous) => pruneCarline(diagnostics, previous));
    }, [diagnostics]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const showToast = useCallback((message: string, iconName = 'checkmark') => {
        setToast({ iconName, message });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(undefined), TOAST_MS);
    }, []);

    /**
     * Switching tab starts that list clean, as the publications tabs do: the keyword and the
     * filters are dropped, and the panel is open on All and collapsed on the other two.
     */
    const selectTab = useCallback(
        (nextTab: SdSearchType) => {
            if (nextTab === searchType) {
                return;
            }
            setSearchType(nextTab);
            setDraftQuery('');
            setDraftFilters({});
            setQuery('');
            setFilters({});
            setIsPanelCollapsed(nextTab !== 'ALL');
        },
        [searchType]
    );

    /**
     * Emptying the box puts the full list back rather than leaving the last keyword's results on
     * screen with an empty box above them. Typing a new keyword still waits for Search.
     */
    const changeQuery = useCallback(
        (text: string) => {
            setDraftQuery(text);
            if (shouldResetQuery(text, query)) {
                setQuery('');
            }
        },
        [query]
    );

    const runSearch = useCallback(() => {
        setQuery(draftQuery.trim());
        setFilters(draftFilters);
        setIsPanelCollapsed(true);
    }, [draftFilters, draftQuery]);

    const clearAll = useCallback(() => {
        setDraftQuery('');
        setDraftFilters({});
        setQuery('');
        setFilters({});
    }, []);

    const hasSomethingToClear = Boolean(
        draftQuery || draftFilters.modelYear || draftFilters.carlineId || query || filters.modelYear
    );

    const recordRecentView = useCallback(
        (documentId: string) => {
            if (!account) {
                return;
            }
            // Fire and forget: the Recently Viewed tab is not worth blocking the open on
            docsService
                .saveDocumentToRecents({ docId: documentId })
                .catch((error) => logger.error('Failed to record the recently viewed manual', { documentId, error }));
        },
        [account]
    );

    const openManual = useCallback(
        async (documentId: string) => {
            recordRecentView(documentId);
            await openDocumentTab(() => fetchCdnDocumentUrlByDocId(documentId, token), onPopupBlocked);
        },
        [onPopupBlocked, recordRecentView, token]
    );

    const openSection = useCallback(
        async (section: ServiceDiagnosticsSection) => {
            recordRecentView(section.documentId);
            await openDocumentTab(async () => section.cdnUrl, onPopupBlocked);
        },
        [onPopupBlocked, recordRecentView]
    );

    const shareManual = useCallback(
        async (bundle: ServiceDiagnosticsBundle) => {
            try {
                // The bookmarkable CDN link, the same one the publications table shares
                const url = await resolveDocumentUrl(
                    { documentId: bundle.documentId, docType: 'Service Diagnostics' },
                    account ?? null,
                    token,
                    'share'
                );
                if (!url) {
                    showToast(
                        t('stisPublications:serviceDiagnostics.shareFailed', 'The link could not be built'),
                        'x-close'
                    );
                    return;
                }
                await navigator.clipboard.writeText(url);
                showToast(t('stisPublications:serviceDiagnostics.linkCopied', 'Link copied to clipboard'));
            } catch (error) {
                logger.error('Failed to share the manual', { documentId: bundle.documentId, error });
                showToast(
                    t('stisPublications:serviceDiagnostics.shareFailed', 'The link could not be built'),
                    'x-close'
                );
            }
        },
        [account, showToast, t, token]
    );

    const shareSection = useCallback(
        async (section: ServiceDiagnosticsSection) => {
            if (!section.cdnUrl) {
                showToast(
                    t('stisPublications:serviceDiagnostics.shareFailed', 'The link could not be built'),
                    'x-close'
                );
                return;
            }
            try {
                await navigator.clipboard.writeText(section.cdnUrl);
                showToast(t('stisPublications:serviceDiagnostics.linkCopied', 'Link copied to clipboard'));
            } catch (error) {
                logger.error('Failed to share the page', { error, sectionId: section.sectionId });
                showToast(
                    t('stisPublications:serviceDiagnostics.shareFailed', 'The link could not be built'),
                    'x-close'
                );
            }
        },
        [showToast, t]
    );

    const toggleFavorite = useCallback(
        async (documentId: string, isFavorite: boolean) => {
            setFavorite(documentId, isFavorite);
            try {
                await vinSessionService.postFavoriteDocument({ queryParameters: { docId: documentId, isFavorite } });
            } catch (error) {
                setFavorite(documentId, !isFavorite);
                logger.error('Failed to save the favourite', { documentId, error });
            }
        },
        [setFavorite]
    );

    /** Fetches one page of hits. `append` is what the modal's scroll uses to add to the list. */
    const loadPreviewPage = useCallback(
        (bundle: ServiceDiagnosticsBundle, pageNumber: number, append: boolean) => {
            // Opening a second manual while the first is still in flight would otherwise let the
            // first response land last and fill the modal with the wrong manual's pages.
            previewRequest.current += 1;
            const requestId = previewRequest.current;
            setIsPreviewLoading(true);
            docsService
                .getServiceDiagnosticsPages({
                    bundleId: bundle.bundleId,
                    // The keyword reads first in the URL, as it does on the other search calls
                    queryParameters: { query, page: pageNumber, size: PREVIEW_SIZE },
                })
                .then((response) => {
                    if (requestId !== previewRequest.current || !response.success) {
                        return;
                    }
                    const hits = response.data.content ?? [];
                    setPreviewSections((previous) => (append ? [...previous, ...hits] : hits));
                    setPreviewTotal(response.data.page?.totalElements ?? hits.length);
                    setPreviewPage(pageNumber);
                })
                .catch((error) => logger.error('Failed to fetch the matching pages', { error }))
                .finally(() => {
                    if (requestId === previewRequest.current) {
                        setIsPreviewLoading(false);
                    }
                });
        },
        [query]
    );

    const openPreview = useCallback(
        (bundle: ServiceDiagnosticsBundle) => {
            setPreviewBundle(bundle);
            setPreviewSections([]);
            setPreviewTotal(bundle.matchCount ?? 0);
            setPreviewPage(0);
            loadPreviewPage(bundle, 0, false);
        },
        [loadPreviewPage]
    );

    /** The modal asks for more once it is scrolled to the bottom, as the publications one does. */
    const loadMorePreview = useCallback(() => {
        if (!previewBundle || isPreviewLoading || previewSections.length >= previewTotal) {
            return;
        }
        loadPreviewPage(previewBundle, previewPage + 1, true);
    }, [isPreviewLoading, loadPreviewPage, previewBundle, previewPage, previewSections.length, previewTotal]);

    /** A keyword search is ordered by relevance, so the column headers only sort while browsing. */
    const sortByColumn = useCallback(
        (column: SdSortColumn) => {
            if (isKeywordSearch) {
                return;
            }
            setSort((current) => nextSort(current, column));
        },
        [isKeywordSearch]
    );

    const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
    const firstRow = totalElements === 0 ? 0 : page * pageSize + 1;
    const lastRow = Math.min(totalElements, (page + 1) * pageSize);
    // Three numbers, like the publications pager, kept around the current page
    const pageNumbers = useMemo(() => {
        const start = Math.max(0, Math.min(page - 1, totalPages - 3));
        return Array.from({ length: Math.min(3, totalPages) }, (_, index) => start + index);
    }, [page, totalPages]);

    const previewLabel = t('stisPublications:table.preview', 'Preview');
    const shareLabel = t('stisPublications:table.share', 'Share');
    const favoriteLabel = t('stisPublications:table.favorite', 'Favorite');
    const removeFavoriteLabel = t('stisPublications:table.removeFavorite', 'Remove Favorite');

    const tabs: { label: string; value: SdSearchType }[] = [
        { label: t('stisPublications:tabs.all', 'All'), value: 'ALL' },
        { label: t('stisPublications:tabs.recentlyViewed', 'Recently Viewed'), value: 'RECENT' },
        { label: t('stisPublications:tabs.favorites', 'Favorites'), value: 'FAVORITE' },
    ];

    const sortArrow = (column: SdSortColumn) => {
        if (isKeywordSearch || sort.column !== column) {
            return null;
        }
        return <Icon color="blueOne" name={sort.direction === 'asc' ? 'chevron-up' : 'chevron-down'} size={14} />;
    };

    const renderEmpty = () => {
        if (hasFailed) {
            return (
                <div className={classes.empty}>
                    <span className={classes.emptyTitle}>
                        {t('stisPublications:serviceDiagnostics.searchFailedTitle', 'The search could not be run')}
                    </span>
                    <span className={classes.emptyBody}>
                        {t(
                            'stisPublications:serviceDiagnostics.searchFailedBody',
                            'Something went wrong on the way to the manuals. Try the search again.'
                        )}
                    </span>
                </div>
            );
        }
        if (searchType === 'FAVORITE') {
            return (
                <div className={classes.empty}>
                    <span className={classes.emptyTitle}>
                        {t('stisPublications:serviceDiagnostics.noFavorites', 'No favourite manuals yet')}
                    </span>
                    <span className={classes.emptyBody}>
                        {t(
                            'stisPublications:serviceDiagnostics.noFavoritesBody',
                            'Star a manual from this list to keep it here.'
                        )}
                    </span>
                </div>
            );
        }
        if (searchType === 'RECENT') {
            return (
                <div className={classes.empty}>
                    <span className={classes.emptyTitle}>
                        {t('stisPublications:serviceDiagnostics.noRecents', 'No manuals opened yet')}
                    </span>
                    <span className={classes.emptyBody}>
                        {t(
                            'stisPublications:serviceDiagnostics.noRecentsBody',
                            'Manuals you open show up here for a while.'
                        )}
                    </span>
                </div>
            );
        }
        return (
            <div className={classes.empty}>
                <span className={classes.emptyTitle}>
                    {isKeywordSearch
                        ? t('stisPublications:serviceDiagnostics.noPages', 'No pages contain “{{query}}”', { query })
                        : t('stisPublications:serviceDiagnostics.noManuals', 'No manuals match these filters')}
                </span>
                <span className={classes.emptyBody}>
                    {isKeywordSearch
                        ? t(
                              'stisPublications:serviceDiagnostics.noPagesBody',
                              'The manuals are there, but nothing inside them matches. Try a different code or keyword, or widen the vehicle.'
                          )
                        : t(
                              'stisPublications:serviceDiagnostics.noManualsBody',
                              'No manual has been published for this vehicle yet.'
                          )}
                </span>
                {hasSomethingToClear && (
                    <button className={classes.emptyAction} onClick={clearAll} type="button">
                        {t('stisPublications:serviceDiagnostics.clearAndSearchAll', 'Clear all and list every manual')}
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className={classes.screen}>
            <div className={classes.tabs} role="tablist">
                {tabs.map((tab) => (
                    <button
                        aria-selected={searchType === tab.value}
                        className={classes.tab}
                        key={tab.value}
                        onClick={() => selectTab(tab.value)}
                        role="tab"
                        type="button"
                    >
                        <Typography
                            color="grayFive"
                            style={searchType === tab.value ? SELECTED_TAB_STYLE : undefined}
                            variant="h6"
                        >
                            {tab.label}
                        </Typography>
                    </button>
                ))}
                {helpPill && <span className={classes.tabsEnd}>{helpPill}</span>}
            </div>

            {guidanceBanner}

            <SdCriteriaPanel
                carlineOptions={carlineOptions}
                collapsed={isPanelCollapsed}
                filters={draftFilters}
                modelYearOptions={modelYearOptions}
                onChange={setDraftFilters}
                onCollapsedChange={setIsPanelCollapsed}
                resultCount={isLoading ? undefined : totalElements}
            />

            <div className={classes.searchRow}>
                <div className={classes.searchInput}>
                    <TextInputHelper
                        onChangeText={changeQuery}
                        onKeyPress={(event) => {
                            if (event.nativeEvent.key === 'Enter') {
                                runSearch();
                            }
                        }}
                        placeholder={t(
                            'stisPublications:serviceDiagnostics.searchPlaceholder',
                            'Search Keywords / Doc Codes / DTC'
                        )}
                        rightIcon={
                            <Icon
                                name={draftQuery ? 'x-close' : 'magnifying-glass'}
                                onPress={draftQuery ? () => changeQuery('') : undefined}
                            />
                        }
                        value={draftQuery}
                    />
                </div>
                <div className={classes.searchActions}>
                    <Button onPress={runSearch} title={t('stisPublications:search', 'Search')} variant="primary" />
                    <Button
                        disabled={!hasSomethingToClear}
                        iconRight={
                            <Icon color={hasSomethingToClear ? 'blueOne' : 'grayFive'} name="x-close" size={16} />
                        }
                        onPress={clearAll}
                        title={t('publications:searchCriteria.clearAll', 'Clear all')}
                        variant="ghost-blue"
                    />
                </div>
            </div>

            {toast &&
                createPortal(
                    <div className={classes.toastLayer}>
                        <ToastSuccessMessage iconColor="white" iconName={toast.iconName} toastData={toast.message} />
                    </div>,
                    document.body
                )}

            {isLoading ? (
                <div aria-busy className={classes.tableWrap} role="status">
                    {SKELETON_ROW_KEYS.map((key) => (
                        <SkeletonLoader height={36} key={key} type="text" width="100%" />
                    ))}
                </div>
            ) : bundles.length === 0 ? (
                renderEmpty()
            ) : (
                <>
                    <div className={classes.tableWrap}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={classes.th}>
                                        {t('stisPublications:table.documentType', 'Document Type')}
                                    </th>
                                    <th className={classes.th}>
                                        <button
                                            className={classes.sortButton}
                                            disabled={isKeywordSearch}
                                            onClick={() => sortByColumn('title')}
                                            type="button"
                                        >
                                            {t('stisPublications:table.titleIdCase', 'Title / ID / Case No')}
                                            {sortArrow('title')}
                                        </button>
                                    </th>
                                    <th className={classes.th}>{t('stisPublications:table.docCode', 'Doc Code')}</th>
                                    <th className={classes.th}>
                                        <button
                                            className={classes.sortButton}
                                            disabled={isKeywordSearch}
                                            onClick={() => sortByColumn('updatedAt')}
                                            type="button"
                                        >
                                            {t('stisPublications:table.updatedDate', 'Updated Date')}
                                            {sortArrow('updatedAt')}
                                        </button>
                                    </th>
                                    <th className={classes.th}>
                                        <button
                                            className={classes.sortButton}
                                            disabled={isKeywordSearch}
                                            onClick={() => sortByColumn('createdAt')}
                                            type="button"
                                        >
                                            {t('stisPublications:table.createdDate', 'Created Date')}
                                            {sortArrow('createdAt')}
                                        </button>
                                    </th>
                                    <th className={classes.th}>{t('stisPublications:table.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bundles.map((bundle) => (
                                    <tr key={bundle.bundleId}>
                                        <td className={classes.td}>
                                            <span className={classes.typeCell}>
                                                <PublicationType publicationType="menuBook" size={20} />
                                                {t(
                                                    'stisPublications:breadcrumbs.serviceDiagnostics',
                                                    'Service Diagnostics'
                                                )}
                                            </span>
                                        </td>
                                        <td className={classes.td}>
                                            <button
                                                className={classes.titleButton}
                                                onClick={() => openManual(bundle.documentId)}
                                                type="button"
                                            >
                                                {bundle.title}
                                            </button>
                                        </td>
                                        <td className={classes.td}>{bundle.docCode ?? '-'}</td>
                                        <td className={classes.td}>{formatDate(bundle.lastUpdated)}</td>
                                        <td className={classes.td}>{formatDate(bundle.createdDate)}</td>
                                        <td className={classes.td}>
                                            <span className={classes.actions}>
                                                <ActionIconButton
                                                    disabled={!isKeywordSearch}
                                                    id={`sd-preview-${bundle.bundleId}`}
                                                    label={previewLabel}
                                                    onClick={() => openPreview(bundle)}
                                                >
                                                    <Eye color={isKeywordSearch ? '#1F518F' : '#8FA3BC'} />
                                                </ActionIconButton>
                                                <ActionIconButton
                                                    id={`sd-share-${bundle.bundleId}`}
                                                    label={shareLabel}
                                                    onClick={() => shareManual(bundle)}
                                                >
                                                    <Share color="#1F518F" />
                                                </ActionIconButton>
                                                <ActionIconButton
                                                    id={`sd-favorite-${bundle.bundleId}`}
                                                    label={bundle.isFavorite ? removeFavoriteLabel : favoriteLabel}
                                                    onClick={() =>
                                                        toggleFavorite(bundle.documentId, !bundle.isFavorite)
                                                    }
                                                >
                                                    {bundle.isFavorite ? <Favourite /> : <FavouriteOpen />}
                                                </ActionIconButton>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={classes.footer}>
                        <Typography variant="label">
                            {t('stisPublications:pagination.range', '{{first}}-{{last}} of {{total}}', {
                                first: firstRow,
                                last: lastRow,
                                total: totalElements,
                            })}
                        </Typography>

                        <div className={classes.pageButtons}>
                            <button
                                className={classes.pageButton}
                                disabled={page === 0}
                                onClick={() => setPage((current) => Math.max(0, current - 1))}
                                type="button"
                            >
                                <Icon color="blueOne" name="chevron-left" size={16} />
                                {t('stisPublications:pagination.previous', 'Prev')}
                            </button>
                            {pageNumbers.map((pageNumber) => (
                                <button
                                    aria-current={pageNumber === page}
                                    className={`${classes.pageButton} ${
                                        pageNumber === page ? classes.pageButtonActive : ''
                                    }`}
                                    key={pageNumber}
                                    onClick={() => setPage(pageNumber)}
                                    type="button"
                                >
                                    {pageNumber + 1}
                                </button>
                            ))}
                            <button
                                className={classes.pageButton}
                                disabled={page + 1 >= totalPages}
                                onClick={() => setPage((current) => current + 1)}
                                type="button"
                            >
                                {t('stisPublications:pagination.next', 'Next')}
                                <Icon color="blueOne" name="chevron-right" size={16} />
                            </button>
                        </div>

                        <select
                            aria-label={t('stisPublications:pagination.perPage', 'Results per page')}
                            className={classes.pageSize}
                            onChange={(event) => setPageSize(Number(event.currentTarget.value))}
                            value={pageSize}
                        >
                            {PAGE_SIZES.map((size) => (
                                <option key={size} value={size}>
                                    {t('stisPublications:pagination.perPageOption', '{{size}} per page', { size })}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}

            <SdPreviewModal
                isLoading={isPreviewLoading}
                manualTitle={previewBundle?.title ?? ''}
                onClose={() => setPreviewBundle(undefined)}
                onLoadMore={loadMorePreview}
                onOpenManual={() => {
                    if (previewBundle) {
                        openManual(previewBundle.documentId);
                    }
                    setPreviewBundle(undefined);
                }}
                onOpenSection={openSection}
                onShareSection={shareSection}
                query={query}
                sections={previewSections}
                show={Boolean(previewBundle)}
                totalElements={previewTotal}
            />
        </div>
    );
};
